import {Component, Inject, Input, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {TranslocoService} from "@ngneat/transloco";
import {ORGANISER_EVENT_COPY, ORGANISER_EVENT_EDIT, ORGANISER_RACE_EDIT} from 'src/app/app-routing.module';

import {
  Category, Country, EventItem, Lookups, Race, RaceAdditionalPricingItem, RaceCommercialItem, RaceDetailsComponentType,
  RaceRoute, RaceScoring, Waypoint,
} from "@model";
import {
  ApplicationFormSettingsService, EventService, RaceRouteService, RaceService
} from "@services";
import {AbstractComponent, NavTabsComponent} from "@components";
import {
  isApplicationFormSettingsValid,
  isEventBasicDataValid,
  isRaceAdditionalDataValid,
  isRaceBasicDataValid,
  isRaceSettingsValid
} from "@utility";
import {isPlatformBrowser} from "@angular/common";

@Component({
  selector: 'app-admin-race-details',
  templateUrl: './admin-race-details.component.html',
  styleUrls: ['./admin-race-details.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class AdminRaceDetailsComponent extends AbstractComponent implements OnInit {
  @Input() fieldConfigTypes: Lookups[] = [];
  @Input() languages: Lookups[] = [];
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = [];
  @Input() sports: Lookups[] = [];
  @Input('raceStatuses') statuses: Lookups[] = [];
  @Input('raceRegistrationTypes') registrationTypes: Lookups[] = [];
  @Input() registrationFeeTypes: Lookups[] = [];
  @Input() visibilityTypes: Lookups[] = [];
  @Input('registrationDisplayFlags') displayFlags: Lookups[] = [];
  @Input() scoringForTypes: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Input() applicationProcedureTypes: Lookups[] = [];
  @Input() typeOfApplicationTypes: Lookups[] = [];
  @Input() teamTypes: Lookups[] = [];
  @Input() selectionOfTeamMembersTypes: Lookups[] = [];
  @Input() measurementTypes: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() startByTypeTypes: Lookups[] = [];
  @Input() genderSeparationTypes: Lookups[] = [];
  @Input() teamRankingTeamSeparationTypes: Lookups[] = [];
  @Input() rotatingOrderTypes: any[] = [];
  @Input() timingSystems: Lookups[] = [];
  @Input() timingEquipments: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() disciplines: Lookups[] = [];
  @Input() sizes: Lookups[] = [];
  @Input() waypointMarkerTypes: Lookups[] = [];
  @Input('event') currentEvent: EventItem | undefined;
  @Input('race') currentRace: Race | undefined;
  @Input() categories: Category[] = [];
  @Input('waypointTypes') currentWaypoints: Lookups[] | undefined;
  @Input() waypointList: Waypoint[] = [];
  @Input() raceRoute: RaceRoute | undefined;

  @Input() raceData?: { race: Race | null, event: EventItem | null, categories: Category[] | null }
  @Input() componentType: RaceDetailsComponentType = RaceDetailsComponentType.ADD;

  currentRaceScoring: RaceScoring | undefined;
  isAdditionalInfoSaved: boolean = true;
  isRaceValid: boolean = false;
  isEventValid: boolean = false;
  isRaceSettingsValid: boolean = false;
  isApplicationFormSettingsValid: boolean = false;

  @ViewChild("nav") nav?: NavTabsComponent;

  constructor(public translocoService: TranslocoService,
              private router: Router, private raceService: RaceService,
              private eventService: EventService, private applicationFormSettingsService: ApplicationFormSettingsService,
              public raceRouteService: RaceRouteService, @Inject(PLATFORM_ID) private platformId: Object) {
    super();
  }

  ngOnInit() {
    this.currentRace = this.currentRace ?? new Race();
    if (isPlatformBrowser(this.platformId)) {
      this.downloadGpx();
    }
    this.initData();
  }

  private downloadGpx() {
    if (this.raceRoute && this.raceRoute.route && this.raceRoute.route !== "") {
      this.raceRouteService.downloadGpx(this.raceRoute.route, (base64data: string) => {
        this.raceRoute!.content = base64data;
      });
    }
  }

  private initData() {
    //Here for changing pageTitle in admin-template.component
    this.raceService.currentRace.next(this.currentRace);
    this.isRaceValid = !!(isRaceBasicDataValid(this.currentRace, this.currentEvent).isValid);
    this.isAdditionalInfoSaved = !!(isRaceAdditionalDataValid(this.currentRace, this.currentEvent).isValid);
    this.isEventValid = !!(isEventBasicDataValid(this.currentEvent).isValid);
    this.currentRaceScoring = this.currentRace?.raceScoring ?? new RaceScoring();
    this.isApplicationFormSettingsValid = !!isApplicationFormSettingsValid(this.currentRace?.applicationFormSettings, this.currentRace?.raceSettings, this.currentEvent).isValid
    this.isRaceSettingsValid = !!(isRaceSettingsValid(this.currentRace?.raceSettings, this.currentEvent).isValid);
  }

  onRaceChange(race: Race | undefined) {
    this.currentRace = race;
    if ((this.currentRace?.id && (this.componentType === RaceDetailsComponentType.ADD || this.componentType === RaceDetailsComponentType.RACE_COPY))) {
      this.router.navigate([ORGANISER_RACE_EDIT], {
        queryParams: {
          eventId: this.currentRace.eventId,
          raceId: this.currentRace.id,
          organiserId: this.currentEvent?.organiserId
        }
      })
    } else {
      this.initData();
    }
  }

  isEventCopyComponent(): boolean {
    return this.componentType === RaceDetailsComponentType.EVENT_COPY;
  }

  disableEditing(): boolean {
    return !(this.isRaceValid) || !(this.isEventValid) || this.isEventCopyComponent();
  }

  navigateToEventBasicInfo(): void {
    if (this.currentEvent && this.currentEvent.id) {
      this.eventService.setCurrentEvent(this.currentEvent);
      this.router.navigate([this.isEventCopyComponent() ? ORGANISER_EVENT_COPY : ORGANISER_EVENT_EDIT], {queryParams: {eventId: this.currentEvent.id}});
    }
  }

  navigateToRaceBasicInfo(): void {
    this.nav?.select(1);
  }


  navigateToRaceSettings(): void {
    this.nav?.select(3)
  }

  onRaceCommercialItemsChanged(raceCommercialItems: RaceCommercialItem[]): void {
    this.raceCommercialItems = raceCommercialItems;
  }

  onRaceAdditionalItemsChanged(raceAdditionalItems: RaceAdditionalPricingItem[]): void {
    this.raceAdditionalItems = raceAdditionalItems;
  }

  onWaypointListChange(waypointList: Waypoint[]): void {
    this.waypointList = waypointList;
  }

  onRaceRouteChange(raceRoute: RaceRoute | undefined): void {
    this.raceRoute = raceRoute;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.raceService.currentRace.next(undefined);
  }
}
